/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

declare global {
    interface Window {
        /**
         * Adobe Client Data Layer (ACDL) queue consumed by the Adobe Experience
         * Platform tag manager (Launch / Tags). Initialized as an array in the
         * document <head> (see root.tsx) before the Launch embed loads, so pushes
         * made before the embed is ready are replayed once it initializes.
         */
        adobeDataLayer?: Array<Record<string, unknown>>;
    }
}

/**
 * Pushes a `page-view` event to the Adobe Client Data Layer on every route change.
 *
 * The Launch embed only fires its native page-load rules on the initial document
 * load. This app is client-routed, so it never triggers a real page load on a
 * client-side navigation — without an explicit push the data layer (and therefore
 * Adobe) would only ever see the first page. This component closes that gap: on
 * mount (the initial page) and on every subsequent `useLocation` change it appends
 * a `page-view` entry that a Launch rule can be triggered on.
 *
 * Consent note: the payload is intentionally non-PII (path/url/title/referrer
 * only) — no shopper identifiers. Whether any tag actually *fires* off this event
 * is gated inside Adobe Launch, which is the consent orchestrator for AEP tags.
 */
export function AdobeDataLayerTracker() {
    const location = useLocation();
    const { adobeLaunch } = useConfig();
    const lastPushedRef = useRef<string | null>(null);

    useEffect(() => {
        // Only track on the client and only when the Adobe embed is configured.
        if (typeof window === 'undefined' || !adobeLaunch?.src) {
            return;
        }

        const fullPath = `${location.pathname}${location.search}${location.hash}`;
        // Guard against a duplicate push for the same URL (e.g. a React StrictMode
        // remount in development re-runs the effect on the same component instance).
        if (lastPushedRef.current === fullPath) {
            return;
        }
        lastPushedRef.current = fullPath;

        // Defensive: the <head> inline script normally creates this array first, but
        // a blocked/dropped inline script must not cause the page-view to be lost.
        const dataLayer = (window.adobeDataLayer = window.adobeDataLayer ?? []);
        dataLayer.push({
            event: 'page-view',
            page: {
                path: location.pathname,
                query: location.search,
                hash: location.hash,
                url: window.location.href,
                title: document.title,
                referrer: document.referrer,
            },
        });
    }, [adobeLaunch?.src, location.pathname, location.search, location.hash]);

    return null;
}
